import React, { useEffect, useRef } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import './HomeBackground.css';

/* ── Iconos SVG internos ── */
const IconArrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const IconChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M4 6l1.5 1.5L8 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconClock = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M6 3v3l2 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const IconShield = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M6 1l4 1.5v3.5C10 8.5 8 10.5 6 11 4 10.5 2 8.5 2 6V2.5L6 1z"
      stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M4 6l1.5 1.5L8 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

/* ══════════════════════════════════════════════════════════
   Panel de Telemetría — Variante C
   Layout vertical compacto: 3 stats → mini-mapa → chart IA
   ══════════════════════════════════════════════════════════ */
const TelemetryPanel = ({ t, dataKey }) => {
  // Stats compactos (3 en fila)
  const stats = [
    {
      label: t(`${dataKey}.sensor_temp_label`, 'Temp.'),
      value: t(`${dataKey}.sensor_temp_value`, '28.4°'),
      tone: 'warn',
    },
    {
      label: t(`${dataKey}.sensor_hum_label`, 'Humedad'),
      value: t(`${dataKey}.sensor_hum_value`, '63%'),
      tone: 'info',
    },
    {
      label: t(`${dataKey}.sensor_rad_label`, 'Radiación'),
      value: t(`${dataKey}.sensor_rad_value`, '842 W/m²'),
      tone: 'warn',
    },
  ];

  // Pines del mini-mapa (sensores en parcelas)
  const pins = [
    { top: '30%', left: '25%', tone: 'accent' },
    { top: '55%', left: '50%', tone: 'info' },
    { top: '40%', left: '75%', tone: 'warn' },
    { top: '70%', left: '62%', tone: 'accent' },
  ];

  return (
    <div className="xAgro-home-panel" aria-label="Vista en vivo de la finca">
      {/* Header — badge LIVE */}
      <div className="xAgro-home-panel-header">
        <span className="xAgro-home-panel-live">
          <span className="xAgro-home-panel-live-dot" aria-hidden="true" />
          {t(`${dataKey}.panel_live`, 'LIVE · Finca El Progreso')}
        </span>
      </div>

      {/* 1. Stats — 3 sensores compactos */}
      <div className="xAgro-home-stats-row">
        {stats.map((s, i) => (
          <div key={i} className="xAgro-home-stat">
            <div className={`xAgro-home-stat-val xAgro-home-stat-val--${s.tone}`}>
              {s.value}
            </div>
            <div className="xAgro-home-stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      {/* 2. Mini-mapa satelital */}
      <div className="xAgro-home-mini-map" aria-hidden="true">
        <span className="xAgro-home-map-tag">
          {t(`${dataKey}.map_tag`, 'Satelite')}
        </span>
        {pins.map((p, i) => (
          <span
            key={i}
            className={`xAgro-home-mini-pin xAgro-home-mini-pin--${p.tone}`}
            style={{ top: p.top, left: p.left, animationDelay: `${i * 0.35}s` }}
          />
        ))}
      </div>

      {/* 3. Chart de ahorro IA */}
      <div className="xAgro-home-chart-block">
        <div className="xAgro-home-chart-info">
          <div className="xAgro-home-chart-lbl">
            {t(`${dataKey}.chart_title`, 'Ahorro de agua · 4 sem')}
          </div>
          <div className="xAgro-home-chart-sub">
            {t(`${dataKey}.chart_sub`, 'vs. plan base')}
          </div>
        </div>
        <div className="xAgro-home-chart-val">
          {t(`${dataKey}.chart_value`, '−24%')}
        </div>
        <svg
          className="xAgro-home-chart-svg"
          viewBox="0 0 200 36"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="xagro-chart-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="var(--xagro-accent)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--xagro-accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0,28 L25,24 L50,26 L80,18 L110,20 L140,14 L170,10 L200,6 L200,36 L0,36 Z"
            fill="url(#xagro-chart-grad)"
          />
          <path
            d="M0,28 L25,24 L50,26 L80,18 L110,20 L140,14 L170,10 L200,6"
            fill="none"
            stroke="var(--xagro-accent)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Footer del panel */}
      <div className="xAgro-home-panel-foot">
        <span className="xAgro-home-panel-foot-dot" aria-hidden="true" />
        <span className="xAgro-home-panel-foot-text">
          {t(`${dataKey}.panel_status`, 'Actualizado hace 12 seg · 4 sensores activos')}
        </span>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Componente principal
   ══════════════════════════════════════════════════════════ */
const HomeBackground = function Home({ data }) {
  const [t] = useTranslation('common');
  const sectionRef = useRef(null);

  // Textos del hero desde common.json → xAgro.home
  const title        = t(`${data}.title`);
  const subtitle     = t(`${data}.subtitle`);
  const badge        = t(`${data}.badge`);
  const ctaPrimary   = t(`${data}.cta_primary`);
  const ctaSecondary = t(`${data}.cta_secondary`);
  const metrics      = t(`${data}.metrics`, { returnObjects: true });
  const trust        = t(`${data}.trust`,   { returnObjects: true });

  // Animación de entrada
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const id = requestAnimationFrame(() => section.classList.add('xAgro-home--visible'));
    return () => cancelAnimationFrame(id);
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    else window.location.hash = '#contact';
  };

  const scrollToFeatures = () => {
    const el = document.getElementById('xAgroResume');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Defaults — los chips de confianza ahora son opcionales pero respetan i18n
  const defaultTrust = [
    { icon: 'check',  text: 'Cobertura de largo alcance' },
    { icon: 'clock',  text: 'Operación sin internet' },
    { icon: 'check',  text: 'Hardware integrado' },
    { icon: 'shield', text: 'IA personalizada' },
  ];
  const trustItems = Array.isArray(trust) ? trust : defaultTrust;

  // Métricas por defecto — incluyen hectáreas monitoreadas
  const defaultMetrics = [
    { value: '500+', label: 'Hectáreas monitoreadas' },
    { value: '40%',  label: 'Reducción de agua' },
    { value: '24/7', label: 'Tiempo real' },
  ];
  const metricItems = Array.isArray(metrics) ? metrics : defaultMetrics;

  const trustIcon = (type) => {
    if (type === 'clock')  return <IconClock />;
    if (type === 'shield') return <IconShield />;
    return <IconCheck />;
  };

  return (
    <section
      id="xAgroHome"
      ref={sectionRef}
      className="xAgro-home-section"
      style={{
        background:
          "rgba(0,0,0,0) url('/assets/img/xAgro/xagro_background.jpg') no-repeat scroll center center / cover",
      }}
    >
      {/* Overlay steel para legibilidad sobre la foto */}
      <div className="xAgro-home-overlay" aria-hidden="true" />

      {/* Patrón de puntos decorativo */}
      <div className="xAgro-home-bg-pattern" aria-hidden="true" />

      <div className="xAgro-home-container">
        <div className="row xAgro-home-row">

          {/* ── Columna izquierda: contenido ── */}
          <div className="col-12 col-lg-7 xAgro-home-content">

            {/* Badge */}
            <div className="xAgro-home-badge">
              <span className="xAgro-home-badge-dot" aria-hidden="true" />
              {badge}
            </div>

            <div className='xAgro-home-img'>
              <img src="/assets/img/xAgro/xagro_brand_transparent.png" alt="xAgro" />
            </div>

            {/* Título */}
            <div className='mx-3 my-3'>
              <h1 className="xAgro-home-title">{title}</h1> <span className="xAgro-home-title-mark">{t(`${data}.title_mark`)}</span>
            </div>
            {/* Subtítulo en chip semitransparente */}
            <div className="xAgro-home-subtitle-wrap">
              <h2 className="xAgro-home-subtitle">{subtitle}</h2>
            </div>

            {/* CTAs */}
            <div className="xAgro-home-cta-group">
              <button className="xAgro-home-cta-primary" onClick={scrollToContact}>
                {ctaPrimary}
                <IconArrow />
              </button>
              <button className="xAgro-home-cta-secondary" onClick={scrollToFeatures}>
                {ctaSecondary}
              </button>
            </div>

            {/* Trust chips (señales de confianza) */}
            <div className="xAgro-home-trust" role="list" aria-label="Garantías">
              {trustItems.map((item, idx) => (
                <div key={idx} className="xAgro-home-trust-item mr-2" role="listitem">
                  <span className="xAgro-home-trust-icon">{trustIcon(item.icon)}</span>
                  <span className="xAgro-home-trust-text">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Métricas: prueba social con hectáreas monitoreadas */}
            <div className="xAgro-home-metrics" role="list" aria-label="Estadísticas clave">
              {metricItems.map((metric, idx) => (
                <div key={idx} className="xAgro-home-metric" role="listitem">
                  <span className="xAgro-home-metric-value">{metric.value}</span>
                  <span className="xAgro-home-metric-label">{metric.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Columna derecha: panel de telemetría (Variante C) ── */}
          <div className="col-lg-5 xAgro-home-visual d-none d-lg-flex">
            <div className="xAgro-home-panel-wrapper">
              <div className="xAgro-home-panel-glow" aria-hidden="true" />
              <TelemetryPanel t={t} dataKey={data} />
            </div>
          </div>

        </div>
      </div>

      {/* Scroll hint */}
      <div className="xAgro-home-scroll-hint" aria-hidden="true" onClick={scrollToFeatures}>
        <span className="xAgro-home-scroll-text">scroll</span>
        <span className="xAgro-home-scroll-icon"><IconChevronDown /></span>
      </div>

    </section>
  );
};

export default withTranslation('common')(HomeBackground);